import React from "react";
import { Icon, IconName, Avatar, AvatarProps } from "@telia/teddy";
import cn from "classnames";
import { NotificationWrapper } from "./NotificationWrapper";
import { useBreakpoint } from "../../../hooks";

type Props = {
    label?: React.ReactNode;
    /**
     * Props to render an avatar in the item.
     */
    avatar?: AvatarProps;

    /**
     * Icon to display in front of item
     */
    icon?: IconName;
    /**
     * Whether the item is currently active.
     * Eg. we are on a page corresponding to an entry from the menu
     */
    active?: boolean;
    /**
     * Renders the item as an anchor tag.
     */
    href?: string;

    /**
     * Action to execute on click item
     */
    onClick: (e: React.SyntheticEvent) => void;

    /**
     * Whether to render the item as an li or div.
     * The outermost tag should always be an <li>.
     *
     * If you need custom logic and markup causing the SideMenuItem
     * to be rendered deeper in the SideMenu, set the  tag='div' on SideMenuItem
     * and set your custom wrapper to an <li>.
     */
    tag?: "div" | "li";

    className?: string;

    color?: "purple" | "grey";

    /**
     * @default 'squared'
     */
    kind?: "rounded" | "squared";

    hasNotification?: boolean;
    numberOfNotifications?: number;

    /**
     * If the item should collapse to only show icons
     * @default true
     */
    collapse?: boolean;
    tabIndex?: number;
    disabled?: boolean;
    dataTrackingId?: string;
    children?: React.ReactNode;
};

export const SideMenuItem: React.FC<Props> = (props) => {
    const {
        onClick,
        active,
        avatar,
        className,
        color,
        hasNotification,
        href,
        icon,
        kind,
        label,
        numberOfNotifications,
        tabIndex,
        tag,
        dataTrackingId,
        children,
        disabled,
    } = props;
    const Tag = disabled ? "div" : href ? "a" : "button";
    const WrapperTag = tag ? tag : "li";
    const collapse = props.collapse ?? true;
    const md = useBreakpoint("md");

    const handleClick = (e: React.SyntheticEvent) => {
        if (disabled) return;
        if (href) {
            e.preventDefault();
        }
        onClick(e);
    };

    return (
        <WrapperTag
            className={cn(
                "side-menu-item",
                {
                    "side-menu-item--active": active,
                    "side-menu-item--grey": color === "grey",
                    "side-menu-item--purple": color === "purple",
                },
                `side-menu-item--${kind ?? "squared"}`,
                className,
            )}
            data-tracking-id={dataTrackingId}
        >
            {md || !collapse ? (
                <Tag
                    className={`side-menu-item__action-element ${
                        disabled ? "side-menu-item__action-element--disabled" : ""
                    }`}
                    onClick={handleClick}
                    href={href}
                    tabIndex={tabIndex ?? 0}
                >
                    {avatar && (
                        <div className="side-menu-item__avatar">
                            <Avatar {...avatar} />
                        </div>
                    )}
                    {icon && (
                        <div className="side-menu-item__icon">
                            <Icon name={icon} />
                        </div>
                    )}
                    <NotificationWrapper
                        hasNotification={hasNotification}
                        numberOfNotifications={numberOfNotifications}
                    >
                        <div className="side-menu-item__label">{label}</div>
                    </NotificationWrapper>
                    {children}
                </Tag>
            ) : (
                <Tag
                    className={`side-menu-item__action-element side-menu-item__action-element--collapse ${
                        disabled ? "side-menu-item__action-element--disabled" : ""
                    }
          `}
                    onClick={handleClick}
                    tabIndex={tabIndex ?? 0}
                    href={href}
                >
                    <NotificationWrapper
                        position="on-top-left"
                        hasNotification={hasNotification}
                        numberOfNotifications={numberOfNotifications}
                    >
                        <>
                            {avatar && (
                                <div className="side-menu-item__avatar">
                                    <Avatar {...avatar} />
                                </div>
                            )}
                            {icon && (
                                <div className="side-menu-item__icon">
                                    <Icon name={icon} />
                                </div>
                            )}
                        </>
                    </NotificationWrapper>
                    <div className="side-menu-item__label--visually-hidden">{label}</div>
                </Tag>
            )}
        </WrapperTag>
    );
};
